/**
 * MovementWidget
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 * Unified with all other widgets in the platform for consistent 50% width layout.
 *
 * Shows bank movement details and allows editing/linking to invoices.
 */

import { useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Building2,
  FileText,
  Link,
  CheckCircle2,
  Circle,
} from 'lucide-react';
import { useAccounts, useInvoices, useMovements } from '@business-assistant/shared';
import type { Movement } from '@business-assistant/shared';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import { Button } from '../common/forms';

// =============================================================================
// TYPES
// =============================================================================

interface MovementWidgetProps {
  movementId?: string;
  onClose: () => void;
  onSave?: (data: MovementFormData) => void;
}

export interface MovementFormData {
  counterparty: string;
  amount: number;
  direction: 'in' | 'out';
  at: string;
  memo?: string;
  reconciled: boolean;
  invoiceId?: string;
  accountId: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function MovementWidget({
  movementId,
  onClose,
  onSave,
}: MovementWidgetProps) {
  const { t } = useTranslation();
  // Data from react-query (canonical) with a Zustand-store fallback for
  const { data: movements = [] } = useMovements();
  const { data: invoices = [] } = useInvoices();
  const { data: accounts = [] } = useAccounts();

  // Find existing movement
  const existingMovement = movementId
    ? movements.find((m) => m.id === movementId)
    : null;

  // Form state
  const [formData, setFormData] = useState<MovementFormData>(() => {
    if (existingMovement) {
      return {
        counterparty: existingMovement.counterparty,
        amount: existingMovement.amount / 100,
        direction: existingMovement.direction,
        at: existingMovement.at.split('T')[0],
        memo: existingMovement.memo,
        reconciled: existingMovement.reconciled,
        invoiceId: existingMovement.invoiceId,
        accountId: existingMovement.accountId,
      };
    }
    return {
      counterparty: '',
      amount: 0,
      direction: 'out',
      at: new Date().toISOString().split('T')[0],
      memo: '',
      reconciled: false,
      invoiceId: undefined,
      accountId: accounts[0]?.id || '',
    };
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSave) {
      onSave({
        ...formData,
        amount: Math.round(formData.amount * 100), // Convert to cents
      });
    }
  };

  const isIncoming = formData.direction === 'in';
  const accentColor = isIncoming ? '#16a34a' : '#dc2626';

  // Get account name
  const accountName = accounts.find((a) => a.id === formData.accountId)?.name || t('movements.account');

  // Get linked invoice
  const linkedInvoice = formData.invoiceId
    ? invoices.find((i) => i.id === formData.invoiceId)
    : null;

  return (
    <DetailPanel
      title={existingMovement ? t('movements.detailTitle') : t('movements.newTitle')}
      subtitle={accountName}
      icon={
        isIncoming ? (
          <TrendingUp size={20} style={{ color: accentColor }} />
        ) : (
          <TrendingDown size={20} style={{ color: accentColor }} />
        )
      }
      onClose={onClose}
      accentColor={accentColor}
      width="50%"
    >
      <form onSubmit={handleSubmit}>
        {/* Direction Toggle */}
        <DetailSection title={t('movements.movementType')}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, direction: 'in' })}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.875rem',
                border: `2px solid ${formData.direction === 'in' ? '#16a34a' : 'var(--line)'}`,
                borderRadius: '0.5rem',
                background: formData.direction === 'in' ? '#dcfce7' : 'white',
                color: formData.direction === 'in' ? '#16a34a' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              <TrendingUp size={18} />
              {t('movements.incoming')}
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, direction: 'out' })}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.875rem',
                border: `2px solid ${formData.direction === 'out' ? '#dc2626' : 'var(--line)'}`,
                borderRadius: '0.5rem',
                background: formData.direction === 'out' ? '#fee2e2' : 'white',
                color: formData.direction === 'out' ? '#dc2626' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              <TrendingDown size={18} />
              {t('movements.outgoing')}
            </button>
          </div>
        </DetailSection>

        {/* Amount */}
        <DetailSection title={t('movements.amountRequired')}>
          <div style={{ position: 'relative' }}>
            <span
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: accentColor,
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              {isIncoming ? '+' : '-'}€
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.amount || ''}
              onChange={(e) =>
                setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })
              }
              required
              placeholder="0.00"
              style={{
                width: '100%',
                padding: '0.75rem',
                paddingLeft: '2.75rem',
                border: `2px solid ${accentColor}40`,
                borderRadius: '0.5rem',
                fontSize: '1.25rem',
                fontWeight: 600,
                color: accentColor,
              }}
            />
          </div>
        </DetailSection>

        {/* Counterparty */}
        <DetailSection title={t('movements.counterpartyRequired')}>
          <div style={{ position: 'relative' }}>
            <Building2
              size={16}
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
              }}
            />
            <input
              type="text"
              value={formData.counterparty}
              onChange={(e) => setFormData({ ...formData, counterparty: e.target.value })}
              required
              placeholder={t('movements.counterpartyPlaceholder')}
              style={{
                width: '100%',
                padding: '0.625rem',
                paddingLeft: '2.5rem',
                border: '1px solid var(--line)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
              }}
            />
          </div>
        </DetailSection>

        {/* Date & Account */}
        <DetailSection title={t('movements.dateAndAccount')}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  marginBottom: '0.375rem',
                }}
              >
                {t('movements.operationDateRequired')}
              </label>
              <div style={{ position: 'relative' }}>
                <Calendar
                  size={16}
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                  }}
                />
                <input
                  type="date"
                  value={formData.at}
                  onChange={(e) => setFormData({ ...formData, at: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '0.625rem',
                    paddingLeft: '2.5rem',
                    border: '1px solid var(--line)',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                  }}
                />
              </div>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  marginBottom: '0.375rem',
                }}
              >
                {t('movements.accountRequired')}
              </label>
              <select
                value={formData.accountId}
                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  border: '1px solid var(--line)',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  background: 'white',
                }}
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </DetailSection>

        {/* Memo */}
        <DetailSection title={t('movements.memoNotes')}>
          <textarea
            value={formData.memo || ''}
            onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
            placeholder={t('movements.memoPlaceholder')}
            rows={3}
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              resize: 'vertical',
            }}
          />
        </DetailSection>

        {/* Link to Invoice */}
        <DetailSection title={t('movements.linkToInvoice')}>
          <select
            value={formData.invoiceId || ''}
            onChange={(e) =>
              setFormData({ ...formData, invoiceId: e.target.value || undefined })
            }
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              background: 'white',
            }}
          >
            <option value="">{t('movements.noLinkedInvoice')}</option>
            {invoices
              .filter((inv) => inv.status !== 'paid' && inv.status !== 'cancelled')
              .map((invoice) => (
                <option key={invoice.id} value={invoice.id}>
                  {invoice.number} - {invoice.contactName} (€
                  {(invoice.totalAmount / 100).toFixed(2)})
                </option>
              ))}
          </select>
          {linkedInvoice && (
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                background: '#dbeafe',
                borderRadius: '0.375rem',
                fontSize: '0.8125rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Link size={14} style={{ color: '#3b82f6' }} />
              <span>
                {t('movements.linkedInvoice')} <strong>{linkedInvoice.number}</strong> - {linkedInvoice.contactName}
              </span>
            </div>
          )}
        </DetailSection>

        {/* Reconciled Status */}
        <DetailSection title={t('movements.reconciliationStatus')}>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, reconciled: !formData.reconciled })}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.875rem 1rem',
              border: `2px solid ${formData.reconciled ? '#16a34a' : 'var(--line)'}`,
              borderRadius: '0.5rem',
              background: formData.reconciled ? '#dcfce7' : 'white',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            {formData.reconciled ? (
              <CheckCircle2 size={20} style={{ color: '#16a34a' }} />
            ) : (
              <Circle size={20} style={{ color: 'var(--text-muted)' }} />
            )}
            <span
              style={{
                fontWeight: 500,
                color: formData.reconciled ? '#16a34a' : 'var(--text-secondary)',
              }}
            >
              {formData.reconciled ? t('movements.reconciled') : t('movements.toReconcile')}
            </span>
          </button>
        </DetailSection>

        {/* Actions */}
        <DetailActions>
          <Button variant="secondary" type="button" onClick={onClose}>
            {t('movements.cancel')}
          </Button>
          <Button variant="primary" type="submit">
            {existingMovement ? t('movements.saveChanges') : t('movements.registerMovement')}
          </Button>
        </DetailActions>
      </form>
    </DetailPanel>
  );
}
